import { useState, useEffect, useCallback } from "react";

import api from "api/api";

//Props Validations
import PropTypes from "prop-types";

//Material UI Components
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Select from "react-select";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import CloseIcon from "@mui/icons-material/Close";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import InsightsIcon from "@mui/icons-material/Insights";

import Tooltip from "@mui/material/Tooltip";

// REDUX
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

import VariableCategoryColorMap from "utils/VariableCategoryColorMap";

// Import common dropdown component and algorithm constants
import DriverAnalysisMethodDropdown, { ALGORITHMS } from "../correlation/DriverAnalysisMethodDropdown";

function DriverAnalysisCreateDialog({ disabled = true, targetVariableOptions = [], productOptions = [], reportData = {} }) {
    const dispatch = useDispatch();

    const backendVariableVariableCategory = useSelector((state) => state.dataMappingStore.backendVariableVariableCategory);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    // Driver analysis
    const [driverAnalysisIdentifiers, setDriverAnalysisIdentifiers] = useState([]);

    const [driverAnalysisFilterModel, setDriverAnalysisFilterModel] = useState(false);
    const [selectedTarget, setSelectedTarget] = useState(null);

    const [identifier, setIdentifier] = useState("");
    const [identifierError, setIdentifierError] = useState(""); // New state for error message

    const [productsSelected, setProductsSelected] = useState([]);

    const [hasMinimumVariables, setHasMinimumVariables] = useState(false);

    // Algorithm dropdown state - using common component
    const [selectedAlgorithm, setSelectedAlgorithm] = useState(null);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // Determine if Beauty and Wellbeing category (Hair Care or Skin Care)
    const isBeautyAndWellbeing = isHairCare || isSkinCare;

    useEffect(() => {
        const exists = driverAnalysisIdentifiers.includes(identifier.toLowerCase().trim());
        if (exists) {
            setIdentifierError("Report with given identifier already exist. Please choose a different identifier.");
        } else {
            setIdentifierError("");
        }
    }, [identifier, driverAnalysisIdentifiers]);

    // Calculate whether we have enough input variables whenever selections change
    useEffect(() => {
        if (!selectedTarget) {
            setHasMinimumVariables(false);
            return;
        }

        // Filter out the target variable and "R_OO" (Overall Opinion)
        const inputVariables = targetVariableOptions.filter((ele) => ele.backendVariable !== selectedTarget.value && ele.backendVariable !== "R_OO").map((ele) => ele.backendVariable);

        setHasMinimumVariables(inputVariables.length < 5);
    }, [selectedTarget, targetVariableOptions]);

    const fetchDriverAnalysisIdentifiers = useCallback(async () => {
        const url = process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/identifiers/all/`;
        try {
            api.get(url)
                .then(async (res) => {
                    const data = res?.data || [];
                    const uniqueIdentifiers = [...new Set(data.map((item) => item.identifier?.toLowerCase().trim()))].filter((identifier) => identifier !== "" && identifier !== null && identifier !== undefined);
                    setDriverAnalysisIdentifiers([...uniqueIdentifiers]);
                })
                .catch(async (error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, []);

    useEffect(() => {
        fetchDriverAnalysisIdentifiers();
    }, [fetchDriverAnalysisIdentifiers]);

    const handleDriverAnalysisFilterModel = () => {
        setDriverAnalysisFilterModel((prev) => !prev);
        resetDriverAnalysisFilterSelection();
    };

    const resetDriverAnalysisFilterSelection = () => {
        setSelectedTarget(null);
        setIdentifier("");
        setProductsSelected([]);
        // Reset algorithm state when filters are reset
        setSelectedAlgorithm(null);
    };

    const returnFiltersPayload = () => {
        let _flt = {};

        _flt["product_codes"] = productsSelected;

        return _flt;
    };

    // Handle algorithm dropdown change - using common component
    const handleAlgorithmChange = (selectedOption) => {
        setSelectedAlgorithm(selectedOption);
    };

    const handleDriverAnalysis = () => {
        if (!selectedTarget || !selectedTarget?.value) {
            handleError("error", "Please select Target Kpi");
            return; // Prevent API call
        }
        if (!identifier) {
            setIdentifierError("Identifier is a mandatory field. Please provide it to create the driver analysis report."); // Set error message
            handleError("error", "Please provide Identifier");
            return; // Prevent API call
        } else {
            setIdentifierError(""); // Clear error message if identifier is valid
        }

        if (productsSelected.length === 0) {
            handleError("error", "Please select products");
            return; // Prevent API call
        }

        const selectedBackendVariableForDriverAnalysis = targetVariableOptions.filter((ele) => selectedTarget?.value !== ele.backendVariable && ele.backendVariable !== "R_OO").map((ele) => ele.backendVariable);
        const targetKpi = selectedTarget.value;
        const targetKpiLabel = selectedTarget.label?.props?.children?.[0] || "";

        async function postDriverAnalysisMetadata() {
            try {
                const _flt = returnFiltersPayload();

                const _cdi = Array.from(new Set(productsSelected?.map((ele) => ele.coreDataId).map(Number))) || [];

                const url = process.env.REACT_APP_SERVER_PATH + `/driver-analysis-output/`;

                const _info = {
                    studyReference: reportData.study_reference,
                    studyPeriod: reportData.study_period,
                    studyProduct: productsSelected,
                };

                const payload = {
                    product_category: productCategoryId,
                    core_data_ids: _cdi.join(","),
                    identifier: identifier,
                    title: identifier,
                    target_kpi: targetKpi,
                    target_kpi_label: targetKpiLabel,
                    corresponding_kpi: selectedBackendVariableForDriverAnalysis.join(","),
                    filters: JSON.stringify(_flt),
                    info: JSON.stringify(_info),
                    algorithm: isBeautyAndWellbeing && selectedAlgorithm ? selectedAlgorithm.value : ALGORITHMS.RANDOM_FOREST_CLASSIFIER.value,
                };

                api.post(url, payload)
                    .then((response) => {
                        const message = response?.data?.message;
                        if (message) {
                            handleError("warning", message, 3000);
                            return;
                        }
                        handleError("success", `Your report ${identifier} will be generated shortly. It will be available within the 'Unsaved' view. You can continue using the tool.`);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
        if (selectedTarget && selectedBackendVariableForDriverAnalysis && selectedBackendVariableForDriverAnalysis.length !== 0 && targetKpi && targetKpi !== "" && targetKpi !== null) {
            if (productCategoryId) {
                postDriverAnalysisMetadata();
                handleDriverAnalysisFilterModel();
            } else {
                handleError("error", "Product category is not selected");
            }
        } else {
            handleError("error", "Target Kpi or Corresponding Kpis is empty.");
        }
    };

    const handleError = (type, error, duration) => {
        dispatch(showNotification({ severity: type, data: error, ...(duration && { duration }) }));
    };

    const handleOnChange = (e, obj) => {
        if (e.target.checked) {
            setProductsSelected((checkedItems) => [...checkedItems, obj]);
        } else {
            const filteredId = productsSelected.filter((ele) => ele.productCode !== obj.productCode);
            setProductsSelected(filteredId);
        }
    };

    return (
        <>
            <Tooltip
                title={disabled ? "The report might not have the most updated backend variables" : ""}
                placement="top-end"
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "white",
                            color: "black",
                            boxShadow: "2px 4px 8px rgba(0,0,0,0.1)",
                            "& .MuiTooltip-arrow": {
                                color: "white",
                            },
                        },
                    },
                }}
                PopperProps={{
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, -15], // Adjust vertical position
                            },
                        },
                    ],
                }}
            >
                <span>
                    <Button
                        size="small"
                        // className="me-4"
                        className={disabled ? "custom-btn disabled me-4" : "custom-btn custom-btn-outlined color-blue me-4"}
                        variant="outlined"
                        startIcon={<TaskOutlinedIcon />}
                        onClick={handleDriverAnalysisFilterModel}
                        disabled={disabled}
                    >
                        Calculate Driver Analysis
                    </Button>
                </span>
            </Tooltip>

            <Dialog
                open={driverAnalysisFilterModel}
                // onClose={handleDriverAnalysisFilterModel}
                fullWidth={true}
                maxWidth="sm"
                PaperProps={{
                    style: {
                        minHeight: "600px",
                    },
                }}
            >
                <DialogTitle
                    style={{
                        backgroundColor: "rgb(51, 94, 234,0.1)",
                        color: "rgb(51, 94, 234)",
                        fontSize: 15,
                        textAlign: "center",
                        padding: "10px 12px",
                    }}
                    // className="liner-light-blue-background-2"
                    id="alert-dialog-title"
                >
                    Driver Analysis Filters
                    <IconButton
                        aria-label="close"
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 2,
                            color: "rgb(51, 94, 234) !important",
                        }}
                        onClick={handleDriverAnalysisFilterModel}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            paddingTop: "20px",
                            minHeight: "400px",
                        }}
                    >
                        {/* Driver Analysis Method Dropdown - Using common component */}
                        <DriverAnalysisMethodDropdown
                            selectedAlgorithm={selectedAlgorithm}
                            onAlgorithmChange={handleAlgorithmChange}
                            margin="0 0 0px 0"
                            width="100%"
                        />

                        <div
                            className=" d-flex"
                            style={{ alignItems: "center" }}
                        >
                            <span
                                className="color-blue"
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    width: "30%",
                                }}
                            >
                                Target Variable:
                            </span>

                            <div
                                style={{
                                    fontSize: "12px",
                                    backgroundColor: "#fff",
                                    width: "70%",

                                    // width: ""
                                }}
                            >
                                <div
                                    className="dropdown-card-dropdown-container"
                                    style={{
                                        padding: 0,
                                    }}
                                >
                                    <Select
                                        styles={{
                                            fontSize: "12px",
                                        }}
                                        name="targetVariable"
                                        placeholder="Target Variable"
                                        onChange={(newValue) => setSelectedTarget(newValue)}
                                        value={selectedTarget}
                                        options={targetVariableOptions.map((datum) => {
                                            return {
                                                value: datum.backendVariable,
                                                label: (
                                                    <>
                                                        {datum.label}
                                                        <span> &nbsp; </span>
                                                        <span
                                                            style={{
                                                                color: VariableCategoryColorMap?.[backendVariableVariableCategory[datum.backendVariable]],
                                                            }}
                                                        >
                                                            <em>{backendVariableVariableCategory[datum.backendVariable]}</em>
                                                        </span>
                                                    </>
                                                ),
                                            };
                                        })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <span
                                className="color-blue"
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    width: "30%",
                                }}
                            >
                                Identifier:
                            </span>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "70%",
                                }}
                            >
                                <input
                                    type="text"
                                    placeholder="Identifier"
                                    className="text-input"
                                    value={identifier}
                                    onChange={(e) => {
                                        setIdentifier(e.target.value);
                                        setIdentifierError("");
                                    }}
                                    style={{
                                        fontSize: "12px",
                                        padding: "4px 12px",
                                        borderRadius: "4px",
                                        border: "1px solid #cccccc",
                                        height: "35px",
                                    }}
                                />
                                {identifierError && <span style={{ color: "red", fontSize: 10 }}>{identifierError}</span>}
                            </div>
                        </div>

                        {/* Product Selection Section */}
                        <div className="d-flex">
                            <Typography
                                className="text-upper-case color-blue"
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    marginRight: "5px",
                                    paddingTop: "8px",
                                    width: "30%",
                                }}
                            >
                                Product Selection:
                            </Typography>
                            <div
                                style={{
                                    // overflowY: "scroll",
                                    border: "1px solid rgba(0, 0, 0, 0.24)",
                                    borderRadius: "5px",
                                    // height: "100%",
                                    // padding: "10px 0px 20px",
                                    width: "70%",
                                }}
                            >
                                <FormGroup>
                                    {productOptions?.map((ele, i) => {
                                        const proTitle = `${ele?.productCode}__${ele?.coreDataId}`;
                                        const proTestCode = productsTestCodeMapData[proTitle];
                                        return (
                                            <FormControlLabel
                                                key={`${i}-${ele?.productCode || ""}`}
                                                sx={{
                                                    "& .MuiTypography-root": {
                                                        fontSize: 12,
                                                        color: "#495057",
                                                        marginTop: "1px",
                                                    },
                                                }}
                                                id={`${i}-${ele?.productCode || ""}`}
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            "& .MuiSvgIcon-root": {
                                                                fontSize: 14,
                                                                // color: "#495057",
                                                            },
                                                        }}
                                                        style={{
                                                            padding: "5px 5px 5px 15px",
                                                        }}
                                                        // color="secondary"
                                                        key={`${i}-${ele?.productCode || ""}`}
                                                        // checked={false}
                                                        onChange={(e) => handleOnChange(e, ele)}
                                                        value={ele?.productCode || ""}
                                                        name={`${i}-${ele?.productCode || ""}`}
                                                    />
                                                }
                                                label={
                                                    ele?.productCode.includes(proTestCode) ? (
                                                        <span>
                                                            {ele?.productCode.split("/")[0] || ""}{" "}
                                                            {isOralCare && (
                                                                <>
                                                                    / <span style={{ color: "#6f42c1" }}>{proTestCode}</span>
                                                                </>
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {ele?.productCode || ""}{" "}
                                                            {isOralCare && (
                                                                <>
                                                                    / <span style={{ color: "#6f42c1" }}>{proTestCode}</span>
                                                                </>
                                                            )}
                                                        </span>
                                                    )
                                                }
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions
                    style={{
                        margin: "0px 16px",
                        flexFlow: "column",
                    }}
                >
                    {/* <div
                        style={{
                            border: "1px solid rgba(0, 0, 0, 0.24)",
                            borderRadius: "5px",
                            padding: "10px",
                            fontSize: 12,
                            display: "block",
                            width: "100%",
                            marginBottom: 10,
                        }}
                    >
                        Note:
                        <ul>
                            <li>No filters selected means all selection.</li>
                            <li>Only Study Period selected means all the Study Reference of the selected Study Period.</li>
                            <li>Only Study period and Study Reference selected means all the products of the selected Study Period and Study Reference.</li>
                        </ul>
                    </div> */}
                    {/* <Button
            size="small"
            className={"custom-btn custom-btn-outlined color-blue"}
            variant="outlined"
            onClick={resetDriverAnalysisFilterSelection}
            startIcon={<RestartAltIcon />}
        >
            Reset
        </Button> */}
                    <p
                        style={{
                            // color: "#FF9800",
                            color: "#495057",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            margin: "0px 10px",
                            textAlign: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        Note - &quot;Overall Opinion&quot; will not be considered as an input variable for driver analysis
                    </p>
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "10px", // Added space between note and button
                        }}
                    >
                        <Tooltip
                            title={
                                hasMinimumVariables ? (
                                    <div
                                        style={{
                                            fontSize: "8px",
                                            textAlign: "center",
                                        }}
                                    >
                                        Driver Analysis cannot be performed <br /> with less than 5 input variables
                                    </div>
                                ) : (
                                    ""
                                )
                            }
                            placement="top-end"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "white",
                                        color: "black",
                                        boxShadow: "2px 4px 8px rgba(0,0,0,0.1)",
                                        "& .MuiTooltip-arrow": {
                                            color: "white",
                                        },
                                    },
                                },
                            }}
                            PopperProps={{
                                modifiers: [
                                    {
                                        name: "offset",
                                        options: {
                                            offset: [0, -15], // Adjust vertical position
                                        },
                                    },
                                ],
                            }}
                        >
                            <span>
                                <Button
                                    disabled={hasMinimumVariables}
                                    className={hasMinimumVariables ? "custom-btn disabled" : "custom-btn custom-btn-outlined color-blue"}
                                    size="small"
                                    variant="outlined"
                                    style={{
                                        margin: 10,
                                    }}
                                    // disabled={disableResultButton}
                                    onClick={handleDriverAnalysis}
                                    startIcon={<InsightsIcon />}
                                >
                                    Generate Driver Analysis Report
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
}

DriverAnalysisCreateDialog.propTypes = {
    disabled: PropTypes.boolean,
    targetVariableOptions: PropTypes.array,
    productOptions: PropTypes.array,
    reportData: PropTypes.object,
};

export default DriverAnalysisCreateDialog;
